import { formatBytes } from "./asset-helpers.js";

// 3.B.3.1: Snapshot-aikajakson time-slider.
//
// V1: HTML range-slider jossa jokainen pysähdys = yksi snapshot. Käyttäjä
// liu'uttaa slider:in haluttuun snapshot:iin → onSelect-callback. Slider:in
// alla näytetään valitun snapshot:in tiedot (aika, koko, tiedostomäärä).
//
// Snapshotit ovat järjestyksessä vanhin → uusin. Slider:in oikea reuna =
// uusin snapshot.

export type SnapshotForSlider = {
  id: string;
  sourcePath: string;
  startTime: string;
  endTime: string;
  fileCount: number;
  totalSize: number;
  description?: string;
};

export type SnapshotTimeSliderProps = {
  snapshots: SnapshotForSlider[];
  selectedIndex: number;
  onSelect: (index: number) => void;
};

export function SnapshotTimeSlider({
  snapshots,
  selectedIndex,
  onSelect,
}: SnapshotTimeSliderProps) {
  if (snapshots.length === 0) {
    return (
      <p className="text-sm text-slate-500">
        Ei snapshotteja saatavilla. Daemon ei ole ehkä vielä luonut ensimmäistä.
      </p>
    );
  }

  const max = snapshots.length - 1;
  const selected = snapshots[selectedIndex] ?? snapshots[max]!;
  const oldest = snapshots[0]!;
  const newest = snapshots[max]!;

  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <input
          type="range"
          min={0}
          max={max}
          step={1}
          value={selectedIndex}
          onChange={(e) => onSelect(Number(e.target.value))}
          className="w-full accent-cyan-500"
          aria-label="Snapshot-aikajakso"
        />
        <div className="flex justify-between text-xs text-slate-500">
          <span>{formatShort(oldest.startTime)}</span>
          <span>{snapshots.length} snapshot:ia</span>
          <span>{formatShort(newest.startTime)}</span>
        </div>
      </div>
      <div className="rounded-md border border-slate-800 bg-slate-900/40 px-4 py-3">
        <p className="text-sm font-medium text-slate-200">
          Valittu: {formatFull(selected.startTime)}
        </p>
        <p className="mt-1 text-xs text-slate-400">
          {selected.sourcePath} ·{" "}
          {selected.fileCount.toLocaleString("fi-FI")} tiedostoa ·{" "}
          {formatBytes(selected.totalSize)}
        </p>
        {selected.description && (
          <p className="mt-1 text-xs italic text-slate-500">{selected.description}</p>
        )}
      </div>
    </div>
  );
}

function formatShort(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("fi-FI", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function formatFull(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("fi-FI", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
